/* eslint-disable no-param-reassign */
/* eslint-disable @typescript-eslint/ban-ts-comment */
import { Icon } from '@zoom/components';
import { format } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import { Point } from 'highcharts';
import { TFunction } from 'i18next';
import React from 'react';
import { renderToStaticMarkup } from 'react-dom/server';

import { StackType, WARNING_BACKGROUND_COLOR } from './config';
import { MissingForecastPerQueue } from '../../types';
import { CalculatedScheduleDataPerQueue } from '../../utils/mapChartData';

export const countValues = (total: number, point: Point) => total + Math.max(point.y!, 0);

export const tooltipFormatter = (
  t: TFunction,
  interval: number,
  timezone: string,
  locale: string,
  localeObj?: Locale,
  dataPerQueue?: CalculatedScheduleDataPerQueue
) =>
  function formatter() {
    // @ts-ignore
    const points = this.points as Point[];
    // @ts-ignore
    const xPosition = this.x;

    if (!points?.length) {
      return false;
    }

    const content = points
      .map((point) => {
        const isRequired = point.series.options.stack === StackType.REQUIRED;
        const isStaffed = point.series.options.stack === StackType.STAFFED;

        const queueId = point.series.options.id?.split('__')?.[0];

        const netStaffed = (isStaffed && queueId && dataPerQueue?.[queueId].netStaff[xPosition]) || 0;

        return `
          <tr class="scheduling-chart__tooltip-item">
            <td>
              <span class="scheduling-chart__tooltip-item-indicator" style="background-color: ${
                // @ts-expect-error: incompatible type 😥
                point.series.options.color
              }"></span>
            </td>
            <td class="scheduling-chart__tooltip-item__label">
              <span style="${isStaffed ? 'font-weight: bold;' : ''}">${t(
          isRequired ? 'form.required' : 'schedule.staffed'
        )}:</span>
            </td>
            <td class="scheduling-chart__tooltip-value">
              <span style="${isStaffed && netStaffed !== 0 ? `color: ${WARNING_BACKGROUND_COLOR};'` : ''}">${
          point.y !== -1 ? point.y?.toLocaleString(locale) : t('layout.notAvailable')
        }</span>
            </td>
          </tr>
        `;
      })
      .join('');

    let status;
    const requiredPoints = points.filter((point) => point.series.options.stack === StackType.REQUIRED);

    if (requiredPoints.filter((point) => point.y! < 0).length) {
      status = '';
    } else {
      const totalStaffedCount =
        points.filter((point) => point.series.options.stack === StackType.STAFFED).reduce<number>(countValues, 0) ?? 0;
      const totalRequiredCount = requiredPoints.reduce<number>(countValues, 0) ?? 0;

      const net = Math.floor((totalStaffedCount - totalRequiredCount) * 100) / 100;
      const isOverStaffed = net > 0;

      status =
        net === 0
          ? `<div class="scheduling-chart__tooltip-status scheduling-chart__tooltip-status--ok">
            ${renderToStaticMarkup(<Icon className="mr-1" color="#28a745" kind="checkmark" size={14} />)}
            ${t('schedule.scheduleOK')}
          </div>`
          : `<div class="scheduling-chart__tooltip-status">
            ${renderToStaticMarkup(
              <Icon
                className="mr-1"
                color={WARNING_BACKGROUND_COLOR}
                kind={isOverStaffed ? 'root-arrow-up' : 'root-arrow-down'}
                size={10}
              />
            )}
            <span>${t(`schedule.${isOverStaffed ? 'over' : 'under'}Staffed`)}:</span>
            <span class="scheduling-chart__tooltip-status__value">${net > 0 ? '+' : ''}${net.toLocaleString(
              locale
            )}</span>
          </div>`;
    }

    const dateContent = `<div>
        ${t('time.time')}: <b>${format(utcToZonedTime(xPosition, timezone), t('date.formats.timeShort'), {
      locale: localeObj,
    })} - ${format(utcToZonedTime(xPosition + interval, timezone), t('date.formats.timeShort'), {
      locale: localeObj,
    })}</b>
       </div>
      `;

    return `
    <div class="scheduling-chart__tooltip-container">
      <div class="scheduling-chart__tooltip-time">${dateContent}</div>
      ${status}
      <table>
        <tbody>
          ${content}
        </tbody>
      </table>
    </div>`;
  };

export const multipleQueueTooltipFormatter = (
  t: TFunction,
  interval: number,
  timezone: string,
  locale: string,
  localeObj?: Locale,
  dataPerQueue?: CalculatedScheduleDataPerQueue,
  missingForecastIntervalsPerQueue?: MissingForecastPerQueue
) =>
  function formatter() {
    // @ts-ignore
    const points = this.points as Point[];
    // @ts-ignore
    const xPosition = this.x;

    if (!points?.length) {
      return false;
    }

    const queuesContent = points
      .map((point) => {
        const isRequired = point.series.options.stack === StackType.REQUIRED;
        const isStaffed = point.series.options.stack === StackType.STAFFED;

        const queueId = point.series.options.id?.split('__')?.[0] ?? '';
        const isMissingForecast = missingForecastIntervalsPerQueue?.[queueId]?.[xPosition] ?? false;
        const netStaffed = (isStaffed && queueId && dataPerQueue?.[queueId].netStaff[xPosition]) || 0;

        if (isRequired) {
          const queueName = point.series.options.name;
          // required series color contains opacity within HEX code, so we need to cut it off
          // @ts-expect-error: incompatible type 😥
          const staffedSeriesColor = (point.series.options.color as string).slice(0, 7);

          return `
        <div class="scheduling-chart__tooltip-multiple-item">
          <div>
              <span class="scheduling-chart__tooltip-item-indicator" style="background-color: ${staffedSeriesColor}"></span>
              <b class="scheduling-chart__tooltip-multiple-item-name">${queueName}</b>
          </div>

          <div class="scheduling-chart__tooltip-multiple-item__label">
              <span>${t('form.required')}: <b style="${
            (isStaffed && netStaffed !== 0) || isMissingForecast ? `color: ${WARNING_BACKGROUND_COLOR};'` : ''
          }">${
            point.y !== -1 ? point.y?.toLocaleString(locale, { maximumFractionDigits: 2 }) : t('layout.notAvailable')
          }
                  </b>
              </span>
             </div>
        </div>
      `;
        }
        return `
          <div class="scheduling-chart__tooltip-multiple-item-staffed">
           ${
             isMissingForecast
               ? `<div class="scheduling-chart__tooltip-multiple-item__missing-forecast">
                  ${renderToStaticMarkup(<Icon className="mr-1" color="#d80225" kind="warning" size={12} />)}
                  ${t('forecast.missingForecast')}
                </div>
              `
               : ''
           }
            <div class="scheduling-chart__tooltip-multiple-item__label">
              <span>${t('schedule.staffed')}:
                <b style="${isStaffed && netStaffed !== 0 ? `color: ${WARNING_BACKGROUND_COLOR};'` : ''}">${
          point.y !== -1 ? point.y?.toLocaleString(locale, { maximumFractionDigits: 2 }) : t('layout.notAvailable')
        }</b>
              </span>
            </div>
          </div>
        `;
      })
      .join('');

    // Algorithm prepares total values over staffed / under staffed values of individual queues within tooltip
    const { underStaffed, overStaffed, totalStaffed, totalRequired } = points.reduce(
      (totalValue, point) => {
        const queueId = point.series.options.id?.split('__')?.[0];
        const isStaffed = point.series.options.stack === StackType.STAFFED;
        const isRequired = point.series.options.stack === StackType.REQUIRED;

        if (isStaffed && queueId && dataPerQueue?.[queueId].netStaff[xPosition] !== undefined) {
          const value = dataPerQueue[queueId].netStaff[xPosition];
          if (value > 0) {
            totalValue.overStaffed += value;
          } else {
            const absValue = Math.abs(value);
            totalValue.underStaffed += absValue;
          }
        }

        if (isRequired) {
          totalValue.totalRequired += Math.max(point.y!, 0);
        }

        if (isStaffed) {
          totalValue.totalStaffed += Math.max(point.y!, 0);
        }
        return totalValue;
      },
      {
        underStaffed: 0,
        overStaffed: 0,
        totalStaffed: 0,
        totalRequired: 0,
      }
    );

    const totalStaffedContent = `
      <div class="scheduling-chart__tooltip-total-staff-required">${t(
        'form.totalRequired'
      )}: <b class="scheduling-chart__tooltip-value">${totalRequired.toLocaleString(locale, {
      maximumFractionDigits: 2,
    })}</b></div>
      <div class="scheduling-chart__tooltip-total-staff-staffed">${t('schedule.totalStaffed')}:
        <b class="scheduling-chart__tooltip-value">${totalStaffed.toLocaleString(locale, { maximumFractionDigits: 2 })}
        </b>
      </div>

      <div class="scheduling-chart__tooltip-total-staff-over-under">${t('schedule.totalOverUnderStaffed')}:
        <b class="scheduling-chart__tooltip-value" style="${
          overStaffed !== 0 ? `color: ${WARNING_BACKGROUND_COLOR};'` : ''
        }">+${overStaffed.toLocaleString(locale, { maximumFractionDigits: 2 })}</b> /

        <b class="scheduling-chart__tooltip-value" style="${
          underStaffed !== 0 ? `color: ${WARNING_BACKGROUND_COLOR};'` : ''
        }">-${underStaffed.toLocaleString(locale, { maximumFractionDigits: 2 })}</b>
      </div>
    `;

    const dateContent = `<div>
          ${t('time.time')}: <b>${format(utcToZonedTime(xPosition, timezone), t('date.formats.timeShort'))} - ${format(
      utcToZonedTime(xPosition + interval, timezone),
      t('date.formats.timeShort'),
      {
        locale: localeObj,
      }
    )}</b>
      </div>
    `;

    return `
      <div class="scheduling-chart__tooltip-container scheduling-chart__tooltip-container--multiple">
        <div class="scheduling-chart__tooltip-time">${dateContent}</div>
        <div class="scheduling-chart__tooltip-queues-wrapper">${queuesContent}</div>
        <div class="scheduling-chart__tooltip-total-staff">${totalStaffedContent}</div>
      </div>`;
  };

export const slaTooltipFormatter = (
  t: TFunction,
  interval: number,
  timezone: string,
  locale: string,
  lastXAxisPosition: number,
  localeObj?: Locale,
  currentHoveredQueueId?: string
) =>
  function formatter() {
    // @ts-ignore
    const points = this.points as Point[];
    // @ts-ignore
    const xPosition = this.x;

    if (!points?.length) {
      // hide tooltip if all series are hidden
      return false;
    }

    if (xPosition === lastXAxisPosition) {
      // hide tooltip if last point selected (doesn't contain real value)
      return false;
    }

    const content = points
      .map((point) => {
        const queueId = point.series.options.id?.split('__')?.[0];

        if (queueId !== currentHoveredQueueId) {
          // skip rendering values of not hovered points! only hovered point and its benchmark value should be visible
          return '';
        }

        return `
            <tr class="scheduling-chart__tooltip-item">
              <td>
                <span class="scheduling-chart__tooltip-item-indicator" style="background-color: ${
                  // @ts-expect-error: incompatible type 😥
                  point.series.options.color
                }"></span>
              </td>
              <td class="scheduling-chart__tooltip-item__label">
                <span">${point.series.name}:</span>
              </td>
              <td class="scheduling-chart__tooltip-value">
                <span>${
                  point.y !== -1
                    ? point.y?.toLocaleString(locale, { maximumFractionDigits: 2, style: 'percent' })
                    : t('layout.notAvailable')
                }</span>
              </td>
            </tr>
          `;
      })
      .join('');

    const dateContent = `<div>
          ${t('time.time')}: <b>${format(utcToZonedTime(xPosition, timezone), t('date.formats.timeShort'), {
      locale: localeObj,
    })} - ${format(utcToZonedTime(xPosition + interval, timezone), t('date.formats.timeShort'), {
      locale: localeObj,
    })}</b>
         </div>
        `;

    return `
      <div class="scheduling-chart__tooltip-container">
        <div class="scheduling-chart__tooltip-time">${dateContent}</div>
        <table>
          <tbody>
            ${content}
          </tbody>
        </table>
      </div>`;
  };
